import { sidebarIconClasses } from '@/@core/components/Sidebar/classes';
import { Application } from '@/@core/models/api';
import { FeatureClient } from '@/@core/services/feature-flag/FeatureClient';
import { HomepageItem, RouteItem, SidebarItem, UseCaseItem } from '@/@core/types/useCases';
import { t, setLocalMessages } from '@/i18n';
import { chatRoute } from '@/modules/AgenticChat/route';
import { agentsRoute } from '@/modules/Agents/route';
import { collectionsRoute } from '@/modules/Collections/route';
import { createRoute } from '@/modules/Create/route';
import { homepageRoute } from '@/modules/Homepage/route';
import { translationRoute } from '@/modules/Translation/route';
import { catchAllUseCaseRoute, useCaseRoute } from '@/modules/UseCase/route';
import { RouteRecordRaw } from 'vue-router';

setLocalMessages('skills');
setLocalMessages('skills-description', 'common');

export const CREATE_YOUR_OWN_SIDEBAR_ITEM: HomepageItem = {
  title: t('SKILLS.create'),
  description: t('SKILLS-DESCRIPTION.create'),
  homePageTitle: t('SKILLS-TITLE.create'),
  route: createRoute,
  icon: `i-material-symbols-add-2 ${sidebarIconClasses}`,
};

export function getEnabledBuiltInSkillUseCaseItems(featureClient: FeatureClient): UseCaseItem[] {
  // Note that the item order is intentional !!!
  const builtInUseCaseItems = [
    {
      title: t('SKILLS.chat'),
      description: t('SKILLS-DESCRIPTION.chat'),
      homePageTitle: t('SKILLS-TITLE.chat'),
      route: chatRoute,
      displayCondition: () => featureClient.chatEnabled(),
      icon: `i-material-symbols-sms-outline ${sidebarIconClasses}`,
    },
    {
      title: t('SKILLS.agents'),
      description: t('SKILLS-DESCRIPTION.agents'),
      homePageTitle: t('SKILLS-TITLE.agents'),
      route: agentsRoute,
      displayCondition: () => featureClient.personalAgentsEnabled(),
      icon: `i-material-symbols-robot-2-outline-rounded ${sidebarIconClasses}`,
    },
    {
      title: t('SKILLS.translate'),
      description: t('SKILLS-DESCRIPTION.translate'),
      homePageTitle: t('SKILLS-TITLE.translate'),
      route: translationRoute,
      displayCondition: () => featureClient.translateEnabled(),
      icon: `i-material-symbols-translate ${sidebarIconClasses}`,
    },
    {
      title: t('SKILLS.collections'),
      description: t('SKILLS-DESCRIPTION.collections'),
      homePageTitle: t('SKILLS-TITLE.collections'),
      route: collectionsRoute,
      displayCondition: () => featureClient.collectionsEnabled(),
      icon: `i-material-symbols-create-new-folder-outline-rounded ${sidebarIconClasses}`,
    },
  ];
  return builtInUseCaseItems.filter((v) => v.displayCondition && v.displayCondition());
}

export function getEnabledExtensibleDefaultAppItems(
  featureClient: FeatureClient,
  usecases: Application[],
  defaultApplications: Application[]
) {
  const summarizeUsecase = usecases.find((item) => item.name?.toLowerCase() === 'summarization');
  const generateUsecase = usecases.find((item) => item.name?.toLowerCase() === 'generate');
  const transcriptionUsecase = usecases.find(
    (item) => item.name?.toLowerCase() === 'transcription'
  );

  // Note that the item order is intentional !!!
  const extensibleDefaultApps = [
    {
      title: t('SKILLS.summarize'),
      description: t('SKILLS-DESCRIPTION.summarize'),
      homePageTitle: t('SKILLS-TITLE.summarize'),
      route: summarizeUsecase?.route,
      displayCondition: () => featureClient.extensibleSummarizeEnabled(),
      icon: undefined,
      iconString: summarizeUsecase?.iconString,
    },
    {
      title: t('SKILLS.generate'),
      description: t('SKILLS-DESCRIPTION.generate'),
      homePageTitle: t('SKILLS-TITLE.generate'),
      route: generateUsecase?.route,
      displayCondition: () => featureClient.extensibleGenerateEnabled(),
      icon: undefined,
      iconString: generateUsecase?.iconString,
    },
    ...defaultApplications.map((item) => ({
      title: item.name,
      description: item.description,
      homePageTitle: item.name,
      displayCondition: () => true,
      route: item.route,
      icon: item.icon as string,
      iconString: item.iconString,
    })),
  ];

  if (transcriptionUsecase?.route) {
    extensibleDefaultApps.unshift({
      title: t('SKILLS.transcribe'),
      description: t('SKILLS-DESCRIPTION.transcribe'),
      homePageTitle: t('SKILLS-TITLE.transcribe'),
      route: transcriptionUsecase.route,
      displayCondition: () => true,
      icon: `i-material-symbols-voice-chat-outline ${sidebarIconClasses}`,
      iconString: transcriptionUsecase.iconString,
    });
  }

  return extensibleDefaultApps.filter((item) => item.displayCondition && item.displayCondition());
}

export function getEnabledBuiltInFeatureSidebarItems(featureClient: FeatureClient): SidebarItem[] {
  const enabledBuiltInUseCases = getEnabledBuiltInSkillUseCaseItems(featureClient);

  // Note that the item order is intentional !!!
  return featureClient.homepageEnabled()
    ? [
        {
          title: 'SKILLS.apps',
          route: homepageRoute,
          icon: `i-material-symbols-apps ${sidebarIconClasses}`,
          displayCondition: () => featureClient.homepageEnabled(),
          description: '',
        },
        ...enabledBuiltInUseCases,
      ]
    : enabledBuiltInUseCases;
}

export function getEnabledFeatureRoutes(featureClient: FeatureClient): RouteRecordRaw[] {
  const routes = [
    ...getEnabledBuiltInSkillUseCaseItems(featureClient),
    CREATE_YOUR_OWN_SIDEBAR_ITEM,
    {
      route: useCaseRoute,
    } as RouteItem,
    {
      route: catchAllUseCaseRoute,
    } as RouteItem,
  ];

  return routes
    .filter(({ displayCondition }) => !displayCondition || displayCondition())
    .map(({ route }) => route);
}
